import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../api";

const GAME_TYPES = ['Singles Stableford','Singles Stableford (Full Handicap)','Singles Medal','Singles Stroke Play','Singles Match Play','Skins','Better Ball Stableford','Better Ball Medal','Better Ball Stroke Play','Better Ball Match Play','Fourball Better Ball Stableford','Team Stableford (Best 2 of 4)','Fourball Match Play','Fourball Stroke Play','Foursomes Stableford','Foursomes Medal','Foursomes Match Play','Greensomes Stableford','Greensomes Medal','Greensomes Match Play','Team Stableford','Team Medal','Team Aggregate Stableford','Team Aggregate Stroke Play','Team Match Play','Texas Scramble (4 players)','Texas Scramble (3 players)','Texas Scramble Medal (4 players)','Texas Scramble Medal (3 players)'];

// "closed" and "full" are computed server-side (effective_status/is_full),
// never stored -- an event can be simultaneously Open and Full (dinner-only
// responses still allowed), so Full is shown as its own badge, not a status.
const STATUS_STYLE = {
  draft:     { bg: "#f3f4f6", color: "#4b5563", label: "Draft" },
  open:      { bg: "#dcfce7", color: "#166534", label: "Open" },
  closed:    { bg: "#fef3c7", color: "#92400e", label: "Closed" },
  current:   { bg: "#dbeafe", color: "#1e40af", label: "Current Competition" },
  completed: { bg: "#ede9fe", color: "#5b21b6", label: "Completed" },
};

const emptyForm = {
  event_name: "", event_date: "", venue: "", course_id: "", game_type: "Stableford",
  hcp_cap: "", first_tee_time: "09:00", tee_interval_minutes: 8, max_players: "",
  closing_date: "",
  payments_required: false, payment_deadline: "", allow_partial_payment: false,
  minimum_deposit_amount: "", allow_offline_payments: true, refund_policy_text: "",
};

export default function Planner() {
  const navigate = useNavigate();
  const [fixtures, setFixtures] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [fxRes, courseRes] = await Promise.all([api.get("/planner"), api.get("/courses")]);
      setFixtures(fxRes.data);
      setCourses(courseRes.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setForm(emptyForm); setShowForm(true); };

  const saveFixture = async () => {
    if (!form.event_name.trim() || !form.event_date) { showToast("Event name and date are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        course_id: form.course_id || null,
        hcp_cap: form.hcp_cap === "" ? null : parseInt(form.hcp_cap, 10),
        max_players: form.max_players === "" ? null : parseInt(form.max_players, 10),
        tee_interval_minutes: parseInt(form.tee_interval_minutes, 10) || 8,
        closing_date: form.closing_date || null,
        payments_required: form.payments_required,
        payment_deadline: form.payment_deadline || null,
        allow_partial_payment: form.allow_partial_payment,
        minimum_deposit_amount: form.minimum_deposit_amount === "" ? null : form.minimum_deposit_amount,
        allow_offline_payments: form.allow_offline_payments,
        refund_policy_text: form.refund_policy_text || null,
      };
      await api.post("/planner", payload);
      setShowForm(false);
      showToast("Fixture created");
      load();
    } catch(e) { console.error(e); showToast("Couldn't save — please check the fields and try again"); }
    finally { setSaving(false); }
  };

  const deleteFixture = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/planner/${confirmDelete.id}`);
      setConfirmDelete(null);
      showToast("Fixture deleted");
      load();
    } catch(e) { showToast(e.response?.data?.error || "Couldn't delete this fixture"); }
    finally { setDeleting(false); }
  };

  const today = new Date(); today.setHours(0,0,0,0);
  const upcoming = fixtures.filter(f => new Date(f.event_date) >= today).sort((a,b) => new Date(a.event_date) - new Date(b.event_date));
  const past = fixtures.filter(f => new Date(f.event_date) < today).sort((a,b) => new Date(b.event_date) - new Date(a.event_date));

  const row = (f) => {
    const st = STATUS_STYLE[f.effective_status] || STATUS_STYLE.draft;
    const placesLeft = f.max_players != null ? Math.max(0, f.max_players - f.confirmed_count) : null;
    return (
      <div key={f.id} onClick={() => navigate(`/planner/${f.id}`)}
        style={{display:"flex",alignItems:"center",gap:"16px",padding:"14px 16px",background:"white",borderRadius:"10px",marginBottom:"8px",cursor:"pointer",boxShadow:"0 1px 3px rgba(0,0,0,0.08)",border:"1px solid #e5e7eb"}}>
        <div style={{flex:"1 1 220px"}}>
          <div style={{fontWeight:700,fontSize:"14px",color:"#14532d"}}>{f.event_name}</div>
          <div style={{fontSize:"12px",color:"#6b7280"}}>{new Date(f.event_date).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",year:"numeric"})} · {f.venue || "no venue set"}</div>
        </div>
        <div style={{fontSize:"12px",color:"#374151",minWidth:"120px"}}>
          ⛳ {f.confirmed_count} confirmed{placesLeft != null && <> · {placesLeft} left</>}
          {f.reserve_count > 0 && <><br/>🕒 {f.reserve_count} on reserve</>}
        </div>
        <div style={{fontSize:"12px",color:"#374151",minWidth:"90px"}}>🍽️ {f.dinner_count}{f.buggy_count > 0 && <><br/>🚗 {f.buggy_count}</>}</div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:"4px"}}>
          <span style={{background:st.bg,color:st.color,fontSize:"11px",fontWeight:700,padding:"4px 10px",borderRadius:"999px",whiteSpace:"nowrap"}}>{st.label}</span>
          {f.is_full && <span style={{background:"#fee2e2",color:"#991b1b",fontSize:"10px",fontWeight:700,padding:"2px 8px",borderRadius:"999px",whiteSpace:"nowrap"}}>FULL</span>}
        </div>
        <button onClick={e => { e.stopPropagation(); setConfirmDelete(f); }}
          style={{background:"#fef2f2",border:"1px solid #fecaca",borderRadius:"6px",color:"#b91c1c",fontSize:"12px",fontWeight:700,cursor:"pointer",padding:"6px 12px",whiteSpace:"nowrap"}}>
          🗑️ Delete
        </button>
      </div>
    );
  };

  return (
    <Layout>
      {toast && <div style={{position:"fixed",bottom:"20px",right:"20px",zIndex:9999}} className="bg-green-600 text-white px-4 py-3 rounded shadow-lg text-sm font-medium">{toast}</div>}
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">📅 Planner</span>
          <span className="text-xs text-gray-500">Plan fixtures ahead of time, let members sign up, then make one the current competition when it's ready.</span>
          <div className="ml-auto">
            <button onClick={openCreate} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">+ New Fixture</button>
          </div>
        </div>

        <div style={{padding:"20px",overflow:"auto",flex:1,background:"#f9fafb"}}>
          {loading ? <div style={{color:"#6b7280",fontSize:"13px"}}>Loading…</div> : (
            <>
              <div style={{fontSize:"13px",fontWeight:700,color:"#374151",marginBottom:"8px"}}>Upcoming</div>
              {upcoming.length === 0 && <div style={{color:"#9ca3af",fontSize:"13px",marginBottom:"20px"}}>No upcoming fixtures yet — click "+ New Fixture" to plan one.</div>}
              {upcoming.map(row)}

              {past.length > 0 && (
                <>
                  <div style={{fontSize:"13px",fontWeight:700,color:"#374151",margin:"24px 0 8px"}}>Past</div>
                  {past.map(row)}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {showForm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"640px",width:"100%",maxHeight:"90vh",overflow:"auto",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#14532d",marginBottom:"16px"}}>📅 New Fixture</div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px"}}>
              <div style={{gridColumn:"1 / -1"}}>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Event name</label>
                <input value={form.event_name} onChange={e => setForm(f => ({...f, event_name: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Date</label>
                <input type="date" value={form.event_date} onChange={e => setForm(f => ({...f, event_date: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Venue</label>
                <input value={form.venue} onChange={e => setForm(f => ({...f, venue: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Course</label>
                <select value={form.course_id} onChange={e => setForm(f => ({...f, course_id: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}}>
                  <option value="">— none —</option>
                  {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Format</label>
                <select value={form.game_type} onChange={e => setForm(f => ({...f, game_type: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}}>
                  {GAME_TYPES.map(g => <option key={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>HCP cap (optional)</label>
                <input type="number" value={form.hcp_cap} onChange={e => setForm(f => ({...f, hcp_cap: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>First tee time</label>
                <input type="time" value={form.first_tee_time} onChange={e => setForm(f => ({...f, first_tee_time: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Tee interval (mins)</label>
                <input type="number" value={form.tee_interval_minutes} onChange={e => setForm(f => ({...f, tee_interval_minutes: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Max players (golf)</label>
                <input type="number" value={form.max_players} onChange={e => setForm(f => ({...f, max_players: e.target.value}))}
                  placeholder="No cap" style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Closing date</label>
                <input type="date" value={form.closing_date} onChange={e => setForm(f => ({...f, closing_date: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div style={{gridColumn:"1 / -1",borderTop:"1px solid #e5e7eb",paddingTop:"12px",fontSize:12,color:"#6b7280"}}>
                Costs (green fees, dinner, buggy, prizes, venue, etc.) are added as individual line items from the fixture's own Costs tab once it's created — this lets you budget everything a real tournament or match involves, not just a fixed set of categories.
              </div>

              <div style={{gridColumn:"1 / -1",borderTop:"1px solid #e5e7eb",paddingTop:"12px"}}>
                <label style={{display:"flex",alignItems:"center",gap:"8px",fontSize:13,fontWeight:700,color:"#374151",cursor:"pointer"}}>
                  <input type="checkbox" checked={form.payments_required} onChange={e => setForm(f => ({...f, payments_required: e.target.checked}))} />
                  Collect payment for this fixture
                </label>
                {form.payments_required && (
                  <div style={{marginTop:"12px",display:"grid",gridTemplateColumns:"repeat(2, 1fr)",gap:"12px",background:"#f9fafb",borderRadius:8,padding:"12px",border:"1px solid #e5e7eb"}}>
                    <div>
                      <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Payment deadline</label>
                      <input type="date" value={form.payment_deadline} onChange={e => setForm(f => ({...f, payment_deadline: e.target.value}))}
                        style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
                    </div>
                    <div>
                      <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Minimum deposit £ (optional)</label>
                      <input type="number" step="0.01" value={form.minimum_deposit_amount} onChange={e => setForm(f => ({...f, minimum_deposit_amount: e.target.value}))}
                        style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
                    </div>
                    <label style={{display:"flex",alignItems:"center",gap:"8px",fontSize:13,color:"#374151",cursor:"pointer"}}>
                      <input type="checkbox" checked={form.allow_partial_payment} onChange={e => setForm(f => ({...f, allow_partial_payment: e.target.checked}))} />
                      Allow partial payment
                    </label>
                    <label style={{display:"flex",alignItems:"center",gap:"8px",fontSize:13,color:"#374151",cursor:"pointer"}}>
                      <input type="checkbox" checked={form.allow_offline_payments} onChange={e => setForm(f => ({...f, allow_offline_payments: e.target.checked}))} />
                      Allow cash/cheque/bank transfer
                    </label>
                    <div style={{gridColumn:"1 / -1"}}>
                      <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Refund policy (optional, shown to players)</label>
                      <textarea value={form.refund_policy_text} onChange={e => setForm(f => ({...f, refund_policy_text: e.target.value}))} rows={2}
                        style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box",fontFamily:"inherit"}} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end",marginTop:"20px"}}>
              <button onClick={() => setShowForm(false)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={saveFixture} disabled={saving}
                style={{background:"#166534",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:saving?0.5:1}}>
                {saving ? "Saving…" : "Create Fixture (Draft)"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"400px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#b91c1c",marginBottom:"8px"}}>⚠️ Delete "{confirmDelete.event_name}"?</div>
            <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>
              This removes the fixture and every signup against it. This can't be undone.
              {confirmDelete.status === "current" && (
                <><br/><br/><strong>Note:</strong> this fixture is linked to Competition #{confirmDelete.created_event_id}. Deleting it here only removes the Planner record — the real competition, its Players/Groups/Scores, are not affected and must be deleted separately from Players if you want those gone too.</>
              )}
            </div>
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => setConfirmDelete(null)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={deleteFixture} disabled={deleting}
                style={{background:"#b91c1c",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:deleting?0.5:1}}>
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
